import type { Floorplan } from '@vibesites/schema';

/**
 * Parse a beds value ("0", "studio", "2", "2 bedroom") into a numeric count
 * (0 = studio). Strips trailing bed/bedroom labels so raw range strings
 * resolve to numbers only.
 */
function parseBedCount(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  let trimmed = String(raw).trim().toLowerCase();
  if (!trimmed) return null;

  trimmed = trimmed.replace(/\s*(?:bed|beds|bedroom|bedrooms)\b\.?$/i, '').trim();
  if (!trimmed) return null;
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 0;

  const num = parseInt(trimmed, 10);
  if (Number.isNaN(num) || num < 0) return null;
  return num;
}

/** Collect unique bedroom counts from floor plans, sorted ascending. */
function bedCountsFromFloorplans(floorplans: Floorplan[] | null | undefined): number[] {
  if (!floorplans?.length) return [];
  const beds = new Set<number>();
  for (const plan of floorplans) {
    const count = parseBedCount(plan.floorplan_beds_min);
    if (count != null) beds.add(count);
  }
  return [...beds].sort((a, b) => a - b);
}

/**
 * Expand a raw bedrooms-range string ("2 - 2", "studio to 3") into discrete
 * bed counts. Equal endpoints collapse to one; unequal endpoints expand as a
 * contiguous inclusive range when both ends parse cleanly.
 */
function bedCountsFromRange(raw: string | null | undefined): number[] {
  if (!raw) return [];
  const trimmed = String(raw).trim();
  if (!trimmed) return [];

  const parts = trimmed
    .split(/\s*(?:[-–—]|to)\s*/i)
    .map((part) => parseBedCount(part))
    .filter((n): n is number => n != null);

  if (!parts.length) return [];
  if (parts.length === 1) return parts;

  const min = Math.min(...parts);
  const max = Math.max(...parts);
  if (min === max) return [min];

  const beds: number[] = [];
  for (let i = min; i <= max; i++) beds.push(i);
  return beds;
}

/**
 * Format bedroom options for display — numbers (and Studio) only:
 *   [2]           → "2"
 *   [0]           → "Studio"
 *   [1, 2, 3]     → "1, 2 & 3"
 *   [0, 1, 2, 3]  → "Studio, 1, 2 & 3"
 */
export function formatBedroomOptionsList(beds: number[]): string | null {
  if (!beds.length) return null;

  if (beds.length === 1) {
    const only = beds[0];
    return only === 0 ? 'Studio' : String(only);
  }

  const parts = beds.map((b) => (b === 0 ? 'Studio' : String(b)));
  if (parts.length === 2) return `${parts[0]} & ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;
}

/**
 * Prefer distinct bedroom types present on floor plans; fall back to expanding
 * the property_bedrooms_range scalar when plans are unavailable.
 */
export function resolveBedroomOptionsLabel(
  floorplans: Floorplan[] | null | undefined,
  range: string | null | undefined,
): string | null {
  return (
    formatBedroomOptionsList(bedCountsFromFloorplans(floorplans)) ||
    formatBedroomOptionsList(bedCountsFromRange(range))
  );
}
